import React, {createRef, useState, useContext, useEffect, useRef} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import axios from "axios";
import __ from "lodash";
import moment from "moment";
import {Checkbox, Icon, useTheme } from "@material-ui/core";
import {
  Block,
  AddShoppingCart,
  DescriptionOutlined,
  Email,
} from "@material-ui/icons";
import MaterialTable from "@material-table/core";
import {defaultComponentsScroll, MaterialTableBody, FullWidthFilter} from "../../../controls/Material-Table-Components"
import SystemContext from "../../../SystemContext";
import {getApiSitePrefix} from "../../../functions/SiteDefaults";
import {ColumnWidth } from "../../../blocks/ColumnHelper";
import useDimensions from "../../../blocks/useDimensions";
import IconButtonMenu from "../../../controls/IconButtonMenu";
import CompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";
import AllocateStockDialog from "./AllocateStockDialog";
import {useAwbStockAllocations, useUpdateAwbStockAllocation}from "../../../services/awbStock";
import { LightTooltip } from '../../../controls';

const apiSitePrefix = getApiSitePrefix();


const AwbStockAllocationsList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const props = {
    ..._props
  }
  const {classes} = props;
  const company = props.company;

  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;
  const queryClient = useQueryClient();

  const [state] = useState({
    activeRow: null,
    selectedIndex: null,
    canEdit: props.canEdit||false,
    canAddOldStock: props.canAddOldStock||false,
  });

  let selectedRow = -1;
  const [searchCriteria, setSearchCriteria] = useState(__.has(props, "searchExtraKeys") && props.searchExtraKeys ? props.searchExtraKeys : ["EMPTY_SEARCH"]);
  const handleUpdateStockAllocation = useUpdateAwbStockAllocation();

  useEffect(() => {
    const setSearchValues = (params) => {
      const {fromDate, toDate, awbPrefix, companyUuid, awbSerial} = params;
      const extraKeys = [];
      extraKeys.push(awbPrefix);
      extraKeys.push(awbSerial??"null");
      extraKeys.push(fromDate??"null");
      extraKeys.push(toDate??"null");
      extraKeys.push(companyUuid);
      setSearchCriteria(extraKeys);
    }

    const refresh = () => {
      queryClient.invalidateQueries(['AwbStockAllocations']);
    }

    if (eventBus) {
      eventBus.bind('AwbStockAllocationsList.search', setSearchValues);
      eventBus.bind('AwbStockAllocationsList.refresh', refresh);
    }
    return () => {
      eventBus.unbind('AwbStockAllocationsList.search', setSearchValues);
      eventBus.unbind('AwbStockAllocationsList.refresh', refresh);
    }
  }, [eventBus, queryClient]);

  const handleSendAllocationEmail = async (rowData) => {

    const {awbAllocationUuid, awbPrefix, fromSerial, toSerial, allocatedToCompanyUuid: companyUuid, companyName, quantity, createdAt} = rowData;

    const url = `${apiSitePrefix}/awbstockallocations/getAllocationExcel/${awbAllocationUuid}`;
    const fromAwbSerial = `${fromSerial}`.padStart(7,'0');
    const toAwbSerial = `${toSerial}`.padStart(7,'0');
    const fromAwb = `${awbPrefix}-${fromAwbSerial}${parseInt(fromAwbSerial) % 7}`;
    const toAwb = `${awbPrefix}-${toAwbSerial}${parseInt(toAwbSerial) % 7}`;
    const issued = moment.utc(createdAt.substring(1,10), 'YYYY-MM-DD').format('DD/MM/YYYY');

    try {
      SystemData.toggleLoadingProgress(true, `Preparing Flight Schedule ...`);
      const response = await axios.get(url, {
        headers: {
          //'Access-Control-Allow-Headers': ['Origin', 'X-Requested-With', 'Content-Type', 'Accept'],
          'Access-Control-Allow-Origin': '*',
        }
      });

      if (response && response.data) {
        SystemData.toggleLoadingProgress(false);
        if (response.data.status) {
          const displayDateFormat = "DD MMM YYYY";
          const startDateMoment = moment(`${props.year}-${props.month}-01`);
          const fileName = response.data.downloadFileName;
          const subject = `Magma Aviation Stock Allocation Notification`;
          const templateType = "STOCK_ALLOCATION_NOTIFICATION";
          const attachmentDescription = `${companyName} Stock Allocation Issued ${issued} from (${fromAwb} to ${toAwb})`;

          const emailRecord = {
            target: "",
            targetFilter: companyUuid,
            recipients: "",
            carbonCopy: "",
            subject: subject,
            templateType: templateType,
            templateUuid: "dummyTemplateUuid",
            attachmentDescription: attachmentDescription,
            attachmentUrl: response.data.excelSignedUrl,
            fileName: fileName.replaceAll("/",""),
            repeatData: [],
            contactRole: "",
            airportCode: undefined,
            from: "",
            fromAwb,
            toAwb,
            awbPrefix,
            issued,
            quantity,
            companyName
          }
          SystemData.sendEmail(emailRecord, `Send Flight Schedule`, true);

          return;
        }
      } else {
        SystemData.openAlert(
          "Send Error",
          `The Stock Allocation could not be sent due to the following error: ${response.data.error}`
        );
      }
    } catch (err) {
      SystemData.toggleLoadingProgress(false);
      SystemData.openAlert(
        "Send Error",
        `The Stock Allocation could not be sent due to the following unexpected error: ${err}`
      );
    }
  }

  const handleNewStockAllocation = (quantityToAllocate, rowData) => {
    return new Promise ((resolve,reject) => {
      try {
        const record = {
          quantity: quantityToAllocate,
          ...__.pick(rowData, ['companyUuid','awbPrefix', 'airportCode', 'allocationNote', 'useStartingAwb', 'startingAwb'])
        }
        const requestStockAllocation = () => {
          axios.put(`${apiSitePrefix}/awbstockallocations/allocate`, record).then(response => {
            if (!response.data) {
              SystemData.alert("Error", `Could not allocate new stock. Server is not responding`);
              resolve(false);
            } else if (response.data.success) {
              SystemData.alert("Info", `Successfully allocated new stock`);
              queryClient.invalidateQueries(["AwbStockAllocations"]);
            } else {
              SystemData.alert("Error", `Could not allocate new stock. ${response.data.error}`);
              resolve(false);
            }
            SystemData.toggleLoadingProgress(false);
            resolve(true);
          }).catch(err => {
            SystemData.alert("Error", `Could not allocate new stock. An unexpected error occurred`);
            console.log('Could not allocate new stock. An unexpected error occurred', err);
            resolve(false);
          });
        }

        const checkStockAllocation = () => {
          const {awbPrefix, companyUuid, airportCode} = rowData;

          axios.get(`${apiSitePrefix}/awbstockallocations/check/${awbPrefix}/${companyUuid}/${airportCode||'null'}`).then((check) => {
            if (check && check.data && check.data.warning) {
              SystemData.confirm(
                "Stock Allocation Check",
                `${check.data.warning}, do you wish to continue?`,
                [
                  {icon: "check_circle_outline", value: true, label: "Yes", options: {color:"primary"}},
                  {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
                ],
                (response) => {
                  if (response) {
                    requestStockAllocation();
                  } else {
                    SystemData.toggleLoadingProgress(false);
                    resolve(true);
                  }
                }
              );
            } else {
              requestStockAllocation();
            }
          }).catch((err) => {
            SystemData.toggleLoadingProgress(false);
            SystemData.alert("Error", `Could not check existing stock. An unexpected error occurred`);
            console.log(`Could not check existing stock`, err);
            resolve(false);
          });
        }
        SystemData.toggleLoadingProgress(true, "Allocating new Air Waybill Stock");

        if (quantityToAllocate > 500) {
          SystemData.confirm(
            "Stock Allocation Quantity Check",
            `You are about to allocate ${quantityToAllocate} Air Waybills, do you wish to continue?`,
            [
              {icon: "check_circle_outline", value: true, label: "Yes", options: {color:"primary"}},
              {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
            ],
            (response) => {
              if (response) {
                checkStockAllocation();
              } else {
                SystemData.toggleLoadingProgress(false);
                resolve(true);
              }
            }
          );
        } else {
          checkStockAllocation();
        }
      } catch (err) {
        SystemData.toggleLoadingProgress(false);
        SystemData.alert("Error", `Could not allocate new stock. Server is not responding`);
        console.log('Could not allocate new stock. An unexpected error occurred', err);
        resolve(false);
      }
    })
  }

  const rowActionOptions = [
    (rowData) => {if (rowData.block) return undefined; return {action: "Block Allocation", itemLabel: "Block Allocation", itemIcon: <Block/>, disabled: rowData.block}},
    (rowData) => {if (!rowData.block) return undefined; return {action: "Unblock Allocation", itemLabel: "Unblock Allocation", itemIcon: <Block/>, disabled: !rowData.block}},
    {action: "Allocate Additional Stock", itemLabel: "Allocate Additional Stock", itemIcon: <AddShoppingCart/>},
    {action: "Send Allocation Email", itemLabel: "Send Allocation Email", itemIcon: <Email/>},
  ];

  async function executeRowAction(params) {
    if (params) {
      const rowData = params.rowData;
      switch (params.selectedOption) {
        case "Block Allocation":
          SystemData.confirm(
            "Confirm Allocation Block",
            "Are you sure you wish to block this allocated AWB stock? If blocked, future bookings for any of the remaining AWBs in this allocation will be rejected",
            [
              {icon: "check_circle", value: true, label: "Block Allocation", options: {color:"primary"}},
              {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
            ],
            async (response) => {
              if (response) {
                handleUpdateStockAllocation.mutate({
                  searchCriteria,
                  record: {...rowData, block: true},
                  postSuccess: async () => {
                    queryClient.invalidateQueries("AwbStockAllocations");
                  },
                  postError: (data) => {
                    if (data.errorMessage) {
                      SystemData.alert("Error", `${data.errorMessage}`);
                    } else {
                      SystemData.alert("Error", `An Unexpected Error has occurred updating the allocation.`);
                    }
                  }
                });
              }
            }
          );
          break;
        case "Unblock Allocation":
          SystemData.confirm(
            "Confirm Allocation Unblock",
            "Are you sure you wish to unblock this allocated AWB stock?",
            [
              {icon: "check_circle", value: true, label: "Unblock Allocation", options: {color:"primary"}},
              {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
            ],
            async (response) => {
              if (response) {
                handleUpdateStockAllocation.mutate({
                  searchCriteria,
                  record: {...rowData, block: false},
                  postSuccess: async () => {
                    queryClient.invalidateQueries("AwbStockAllocations");
                  },
                  postError: (data) => {
                    if (data.errorMessage) {
                      SystemData.alert("Error", `${data.errorMessage}`);
                    } else {
                      SystemData.alert("Error", `An Unexpected Error has occurred updating the allocation.`);
                    }
                  }
                });
              }
            }
          );
          break;
        case "Allocate Additional Stock":
          if (eventBus) {
            eventBus.trigger('AllocateStockDialog.open', {title: "Allocate new AWB Stock", canEdit: state.canEdit, currentAllocation: rowData});
          }
          break;
        case "Send Allocation Email":
          handleSendAllocationEmail(rowData);
          break;
      default:
          break;
      }
    }
  }

  const handleRowClick = (event, rowData) => {
    if (rowData.tableData.id >= 0) {
      selectedRow = rowData.tableData.id;
      if (eventBus) {
        eventBus.trigger('AwbAllocatedStockList.search', rowData);
      }
    }
  }

  const renderActionIcon = (rowData) => {
    return (
      <Icon style={{marginTop: 2, marginLeft: 10}} fontSize="large" color={(state.canEdit ? (rowData.tableData.id === selectedRow ? 'secondary' : 'primary') : 'disabled')}>change_circle</Icon>
    );
  }

  const tableRef = createRef({state: {lastEditingRow: false}});
  let extraKeys = searchCriteria;
  const stockAllocations = useAwbStockAllocations({tableRef, queryClient, extraKeys});

  const getColumns = (tableWidth) => {
    const baseColumn = {
      cellStyle: { fontSize:11, padding: "4px 2px" },
      headerStyle: { padding: "4px 2px" },
      filterCellStyle: { padding: "4px 2px" },
      editable: "never",
      sorting: false,
      resizable: false,
    };
    const borderedCellStyle = { fontSize: 11, padding: "4px 1px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #f5f5f5" };
    const invisibleBorderedCellStyle = { fontSize: 11, padding: "4px 1px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #ffffff" };
    const boldInvisibleBorderedCellStyle = { fontSize: 13, fontWeight: 800, padding: "4px 1px", borderLeft: "0px solid #f5f5f5", borderRight: "1px solid #ffffff" };
    const borderedHeaderStyle = { borderLeft: "0px solid rgb(63, 63, 63)", borderRight: "1px solid rgb(63, 63, 63)" };
    const borderedFilterStyle = {}; //{ borderLeft: "0px solid #ffffff", borderRight: "1px solid #ffffff" };

    return [
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */
      {
        ...baseColumn,
        ...ColumnWidth(70, 4, undefined, {...borderedCellStyle, borderLeft: "1px solid #f5f5f5"}, borderedHeaderStyle, borderedFilterStyle, false),
        title: "Issued On",
        field: "dateAllocated",
        filterComponent: FullWidthFilter,
        render: rowData => {
          return (<span>{moment(rowData.dateAllocated).format("DD/MM/YYYY")}</span>)
        }
      },
      {
        ...baseColumn,
        ...(ColumnWidth(330, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "Allocated to",
        field: "allocatedToCompanyUuid",
        editable: "never",
        emptyValue: null,
        initialEditValue: null,
        filterComponent: FullWidthFilter,
        render: (rowData) => (
          company && !__.isEmpty(company)
            ?
              <span>{rowData.companyName}</span>
            :
              <CompanyUuidLookup
                value={rowData.allocatedToCompanyUuid}
                type="render"
              />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(40, 2, undefined, {...borderedCellStyle, borderLeft: "1px solid #f5f5f5"}, borderedHeaderStyle, borderedFilterStyle, false),
        title: "Airport",
        field: "airportCode",
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, 2, undefined, {...borderedCellStyle, borderLeft: "1px solid #f5f5f5"}, borderedHeaderStyle, borderedFilterStyle, false),
        title: "Quantity",
        field: "quantity",
        type: "numeric",
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(80, 1, undefined, {...borderedCellStyle, borderLeft: "1px solid #f5f5f5"}, borderedHeaderStyle, borderedFilterStyle, false),
        title: "From Serial",
        field: "fromSerial",
        type: "numeric",
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(75, 1, undefined, {...borderedCellStyle, borderLeft: "1px solid #f5f5f5"}, borderedHeaderStyle, borderedFilterStyle, false),
        title: "To Serial",
        field: "toSerial",
        type: "numeric",
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(55, 1, undefined, {...borderedCellStyle, borderLeft: "1px solid #f5f5f5"}, borderedHeaderStyle, borderedFilterStyle, false),
        title: "Used",
        field: "used",
        type: "numeric",
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(75, 1, undefined, {...borderedCellStyle, borderLeft: "1px solid #f5f5f5"}, borderedHeaderStyle, borderedFilterStyle, false),
        title: "Available",
        field: "used",
        type: "numeric",
        filterComponent: FullWidthFilter,
        render: rowData => <span>{rowData.quantity - rowData.used}</span>
      },
      {
        ...baseColumn,
        ...ColumnWidth(30),
        title: "Note",
        field: "bookingUuid",
        filtering: false,
        render: rowData => {
          if (rowData.allocationNote) {
            return (
              <LightTooltip title={rowData.allocationNote} arrow placement="top">
                <DescriptionOutlined style={{paddingLeft: 8}}/>
              </LightTooltip>
            )
          } else {
            return null;
          }
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(50, [8,2]),
        title: "Block",
        field: "block",
        type: "boolean",
        sorting: false,
        emptyValue: false,
        initialEditValue: false,
        render: rowData => <Checkbox style={{padding: 12, paddingLeft: 16}} checked={rowData.block === true} value="true" disabled={company && !__.isEmpty(company)}/>
      },
      {
        ...baseColumn,
        ...ColumnWidth(50),
        title: "Action",
        field: "bookingUuid",
        render: (rowData)=> {
          const rowActions = __.filter(rowActionOptions.map(item => {
            if (typeof item === "function") {
              return item(rowData);
            }
            return item;
          }),item => !__.isEmpty(item));
          return(
            <IconButtonMenu
              style={{ color: theme.palette.primary.main }}
              hidden={false}
              disabled={!state.canEdit || state.multipleSelectionActive>0}
              icon={renderActionIcon(rowData)}
              size={"small"}
              offset={-1}
              tooltip={"Stock Allocation Actions"}
              menuItems={rowActions}
              callback={executeRowAction}
              callbackParams={{rowData: rowData}}
            />
          )
        },
        filterComponent: (props)=> { return(<></>) }
      }
    ];
  };

  const [tableContainerRef, { width: tableWidth, height: tableHeight}] = useDimensions({});
  const theader = tableContainerRef.current?.querySelector('thead');
  const thead = theader ? theader.clientHeight:46;
  const bodyHeight = tableHeight - thead;

  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, position: "relative"}}>
      <div style={{background: "#ffffff", paddingTop: 16, paddingLeft: 20, paddingRight: 4, nowidth: "calc(100% - 24px)"}}>
        <div ref={tableContainerRef} className={state.canEdit?classes.bookingQueueListEdit:classes.bookingQueueList} style={{width: "100%", minWidth: "100%", height: `calc(100vh - ${props.height + 16}px)`}}>
          <MaterialTable
            icons={{ Filter: React.forwardRef((props, ref) => <Icon className="filterIcon">filter_list</Icon> )}}
            tableRef={tableRef}
            style={{
              border: "none", //"1px solid #aaaaaa",
              boxShadow: "none",
              borderRadius: 0,
            }}
            onRowClick={handleRowClick}
            options={{
              highlightRowStyle: {backgroundColor: "#F69259"},
              actionsColumnIndex: -1,
              cellStyle: {
                padding: "4px 2px",
              },
              filterCellStyle: {
                padding: "4px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: 2,
                height: 46
              },
              filterRowStyle: {
                paddingLeft: 0,
                width: "100%",
              },
              toolbar: false,
              toolbarButtonAlignment: "right",
              detailPanelType: "single",
              padding: 'dense',
              paging: false,
              filtering: true,
              search: false,
              searchFieldAlignment: "left",
              columnResizable: false,
              selection: false,
            }}
            components={{
              ...defaultComponentsScroll,
              Body: (props) => (
                <MaterialTableBody
                  {...props}
                  rowHeight={46}
                  overscanRowCount={10}
                  addVirtualScrolling={true}
                  bodyHeight={bodyHeight}
                  headerHeight={thead}
                  filterRowHeight={55}
                />
              ),
            }}
            localization={{
              header: {
                actions: 'Action'
              },
            }}
            title="Allocations"
            columns={getColumns(tableWidth)}
            data={stockAllocations.data}
          />
          <AllocateStockDialog
            canAddOldStock={state.canAddOldStock}
            classes={classes}
            systemData={SystemData}
            eventBus={SystemData.eventBus}
            onClose={(quantityToAllocate, currentAllocation, record) => {
              if (quantityToAllocate && !__.isEmpty(currentAllocation)) {
                return handleNewStockAllocation(quantityToAllocate, {...currentAllocation, companyUuid: currentAllocation.allocatedToCompanyUuid, allocationNote: record.allocationNote}, false);
              } else {
                const newAllocation =
                {
                  ...__.pick(record, ['companyUuid', 'awbPrefix', 'airportCode', 'allocationNote', 'useStartingAwb', 'startingAwb'])
                }
                return handleNewStockAllocation(quantityToAllocate, newAllocation);
              }
            }}
          />
        </div>
      </div>
    </div>
  );

}

export default AwbStockAllocationsList;
